import React, { useCallback } from "react";

import { faStreetView } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { EntitiesSupportingStreetView, Lead } from "@mapmycustomers/shared/types/entity";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import ButtonLink from "@app/component/ButtonLink";
import RoundButton from "@app/component/RoundButton";
import StreetViewModal from "@app/component/StreetViewModal";

interface Props {
  asLink?: boolean;
  disabled?: boolean;
  entity?: EntitiesSupportingStreetView;
  lead?: Lead;
  onClick?: () => void;
}

const messages = defineMessages({
  streetView: {
    id: "actions.streetView",
    defaultMessage: "Street View",
    description: "Street view button",
  },
});

const StreetView: React.FC<Props> = ({ asLink, disabled, entity, lead, onClick }) => {
  const intl = useIntl();
  const [streetViewVisible, showStreetView, hideStreetView] = useBoolean(false);

  const coordinates = entity?.geoPoint
    ? convertGeoPointToLatLngLiteral(entity.geoPoint)
    : lead?.geometry.location;

  const handleClick = useCallback(() => {
    if (coordinates) {
      showStreetView();
      onClick?.();
    }
  }, [coordinates, onClick, showStreetView]);

  return (
    <span>
      {asLink ? (
        <ButtonLink disabled={disabled} onClick={handleClick}>
          {intl.formatMessage(messages.streetView)}
        </ButtonLink>
      ) : (
        <RoundButton
          disabled={disabled}
          icon={<FontAwesomeIcon icon={faStreetView} />}
          onClick={handleClick}
        >
          {intl.formatMessage(messages.streetView)}
        </RoundButton>
      )}
      {streetViewVisible && (
        <StreetViewModal
          coordinates={coordinates}
          entity={entity}
          lead={lead}
          onHide={hideStreetView}
          open={streetViewVisible}
        />
      )}
    </span>
  );
};

export default StreetView;
